import { selectRegionId } from 'shared/selectors/location';
import { getOffers } from 'shared/selectors/offers';
import { selectJsonQuery } from 'shared/selectors/results';
import { fetchEtazhiBlackFridayDataIfNeeded } from 'shared/services/fetchEtazhiBlackFridayDataIfNeeded';
import { fetchEtazhiBlackFridayOffersPrices } from 'shared/services/fetchEtazhiBlackFridayOffersPrices';
import { fetchEtazhiBlackFridayShowByRegion } from 'shared/services/fetchEtazhiBlackFridayShowByRegion';
import { actionGenerator } from 'shared/utils/redux';

import type { IOffer } from 'shared/offer';
import type { IEtazhiBlackFridayPricesData } from 'shared/types/etazhiBlackFriday';
import type { ITypedReduxAction, TThunkAction } from 'shared/types/redux';

export enum EEtazhiBlackFridayActionTypes {
  SetPromoOffersData = 'EtazhiBlackFriday/SetPromoOffersData',
  SetPricesData = 'EtazhiBlackFriday/SetPricesData',
  SetShowByRegion = 'EtazhiBlackFriday/SetShowByRegion',
}

type TEtazhiBlackFridayDataSetAction = ITypedReduxAction<
  EEtazhiBlackFridayActionTypes.SetPromoOffersData,
  IOffer[] | null
>;

type TEtazhiBlackFridayPricesDataSetAction = ITypedReduxAction<
  EEtazhiBlackFridayActionTypes.SetPricesData,
  IEtazhiBlackFridayPricesData | null
>;

type TEtazhiBlackFridayShowByRegionAction = ITypedReduxAction<EEtazhiBlackFridayActionTypes.SetShowByRegion, boolean>;

export type TEtazhiBlackFridayDataActions =
  | TEtazhiBlackFridayDataSetAction
  | TEtazhiBlackFridayPricesDataSetAction
  | TEtazhiBlackFridayShowByRegionAction;

export const setEtazhiBlackFridayData = actionGenerator<
  EEtazhiBlackFridayActionTypes.SetPromoOffersData,
  IOffer[] | null
>(EEtazhiBlackFridayActionTypes.SetPromoOffersData);

export const setEtazhiBlackFridayPricesData = actionGenerator<
  EEtazhiBlackFridayActionTypes.SetPricesData,
  IEtazhiBlackFridayPricesData | null
>(EEtazhiBlackFridayActionTypes.SetPricesData);

export const setEtazhiBlackFridayShowByRegion = actionGenerator<EEtazhiBlackFridayActionTypes.SetShowByRegion, boolean>(
  EEtazhiBlackFridayActionTypes.SetShowByRegion,
);

/**
 * Код появился к проекту "Чёрная пятница" для компании "Этажи".
 * @todo: удалить в задаче CD-250843
 */
export function getEtazhiBlackFridayPromoOffers(): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const { logger } = context;

    const state = getState();

    const jsonQuery = selectJsonQuery(state);

    try {
      const promoOffersData = await fetchEtazhiBlackFridayDataIfNeeded(context, {
        jsonQuery,
        getState,
      });

      dispatch(setEtazhiBlackFridayData(promoOffersData?.offersSerialized ?? null));
    } catch (error) {
      logger.error(error);
      dispatch(setEtazhiBlackFridayData(null));
    }
  };
}

export function getEtazhiBlackFridayPricesData(): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const state = getState();

    const jsonQuery = selectJsonQuery(state);
    const realtyOfferIds = getOffers(state).map(offer => offer.id);

    const pricesData = await fetchEtazhiBlackFridayOffersPrices(context, {
      jsonQuery,
      getState,
      realtyOfferIds,
    });

    dispatch(setEtazhiBlackFridayPricesData(pricesData));
  };
}

/**
 * Получаем флаг показа в зависимости от региона
 */
export function getEtazhiBlackFridayShowByRegion(): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const state = getState();

    const regionId = selectRegionId(state);
    const jsonQuery = selectJsonQuery(state);

    const response = await fetchEtazhiBlackFridayShowByRegion({
      context,
      parameters: { regionId, jsonQuery, getState },
    });

    dispatch(setEtazhiBlackFridayShowByRegion(response));
  };
}
